import { SocialPostType } from "@contracts/contentCalendar";
import { SocialPostPublishType } from "@contracts/shareStop";
import { SocialChannels } from "@contracts/socialNetworks";
import { MAX_CAPTION_LENGTH, MAX_PHOTOS_PER_POST } from "@/constants/social";
import FileUtils from "@logic/FileUtils";
import { getImageResolution, getRemoteImageDimensions, getRemoteVideoDimensions, getVideoResolution, ImgOrVideoResolution } from "@helpers/GetImageOrVideoResolution";
import { getAdventureVideoPath } from "@helpers/GenerateGallerySrc";

export type ValidationResult = { ok: true } | { ok: false; message: string };

/** Meta Instagram Reel/Story limits (Bundle publishing spec). */
export const INSTAGRAM_META_LIMITS = {
  textMaxChars: 2000,
  allowedTypes: ["POST", "REEL", "STORY"],
  bundleAcceptedImageFormats: ["jpg", "jpeg", "png", "gif"],
  bundleAcceptedVideoFormats: ["mp4", "mov"],
  bundleMaxImageUploadSizeMb: 25,
  recommendedStoryAndReelAspectRatio: "9:16",
  reel: {
    mediaCountMin: 1,
    mediaCountMax: 1,
    allowedMediaTypes: ["video"],
    video: {
      durationMinSeconds: 3,
      durationMaxSeconds: 900,
      maxWidthPx: 1920,
      recommendedAspectRatio: 0.5625,
    },
  },
  story: {
    mediaCountMin: 1,
    mediaCountMax: 1,
    allowedMediaTypes: ["image", "video"],
    image: {
      bundleMaxFileSizeMb: 8,
      aspectRatioMax: 1.6666667,
      recommendedAspectRatio: 0.5625,
    },
    video: {
      bundleMaxFileSizeMb: 100,
      durationMinSeconds: 3,
      durationMaxSeconds: 60,
      maxWidthPx: 1920,
      aspectRatioMax: 1.6666667,
      recommendedAspectRatio: 0.5625,
    },
  },
} as const;

/** Documented rules not enforced yet — see plan for wiring order. */
export const FUTURE_INSTAGRAM_RULES = {
  videoDuration: {
    reelSeconds: [INSTAGRAM_META_LIMITS.reel.video.durationMinSeconds, INSTAGRAM_META_LIMITS.reel.video.durationMaxSeconds],
    storySeconds: [INSTAGRAM_META_LIMITS.story.video.durationMinSeconds, INSTAGRAM_META_LIMITS.story.video.durationMaxSeconds],
  },
  videoMaxWidthPx: INSTAGRAM_META_LIMITS.reel.video.maxWidthPx,
  fileSizeMb: {
    storyImage: INSTAGRAM_META_LIMITS.story.image.bundleMaxFileSizeMb,
    storyVideo: INSTAGRAM_META_LIMITS.story.video.bundleMaxFileSizeMb,
    bundleImage: INSTAGRAM_META_LIMITS.bundleMaxImageUploadSizeMb,
  },
  storyAspectRatioApiMax: INSTAGRAM_META_LIMITS.story.image.aspectRatioMax,
  recommendedAspectRatio9x16: INSTAGRAM_META_LIMITS.recommendedStoryAndReelAspectRatio,
  videoCodecs: ["h264", "hevc"],
  audioCodec: "aac",
  bitrate: "low priority — defer",
  reelOptionalFields: {
    collaboratorsMax: 3,
    usernameMaxChars: 30,
  },
  storyAccountRequirement: "Instagram Business account required for Stories via Meta content publishing.",
} as const;

export const REEL_STORY_MAX_ASPECT_RATIO = 1.6;

export const PORTRAIT_OR_SQUARE_MESSAGE = "Reels and Stories require media with aspect ratio up to 1.6:1.";

export type AlbumMediaPostType = SocialPostType | SocialPostPublishType;

export function isReelPostType(postType: AlbumMediaPostType): boolean {
  return postType === SocialPostType.Reel || postType === SocialPostPublishType.Reel;
}

export function isStoryPostType(postType: AlbumMediaPostType): boolean {
  return postType === SocialPostType.Story || postType === SocialPostPublishType.Story;
}

export function filterAlbumAssetsForPostType(assetIds: string[], postType: AlbumMediaPostType): string[] {
  if (isReelPostType(postType)) {
    return assetIds.filter(assetId => FileUtils.isVideoFileType(assetId));
  }
  if (isStoryPostType(postType)) {
    return assetIds;
  }
  return assetIds.filter(assetId => !FileUtils.isVideoFileType(assetId));
}

export function getMaxMediaCount(postType: AlbumMediaPostType): number {
  if (isReelPostType(postType) || isStoryPostType(postType)) {
    return 1;
  }
  return MAX_PHOTOS_PER_POST;
}

export function isSingleMediaPostType(postType: AlbumMediaPostType): boolean {
  return isReelPostType(postType) || isStoryPostType(postType);
}

export function isStoryVideoAllowed(postType: AlbumMediaPostType): boolean {
  return isStoryPostType(postType);
}

export function isVideoUploadAllowedForPostType(postType: AlbumMediaPostType): boolean {
  return isReelPostType(postType) || isStoryPostType(postType);
}

export function isVideoOnlyUploadForPostType(postType: AlbumMediaPostType): boolean {
  return isReelPostType(postType);
}

export function getMediaSectionLabel(postType: AlbumMediaPostType): string {
  if (isVideoOnlyUploadForPostType(postType)) {
    return "Videos";
  }
  if (isStoryPostType(postType)) {
    return "Photos and Videos";
  }
  return "Photos";
}

export function getUploadMediaLabel(postType: AlbumMediaPostType): string {
  if (isVideoOnlyUploadForPostType(postType)) {
    return "Upload Videos";
  }
  if (isStoryPostType(postType)) {
    return "Upload Photos and Videos";
  }
  return "Upload Photos";
}

export function selectOrDeselectMediaAsset(selectedIds: string[], assetId: string, isSelected: boolean, maxMediaCount: number): string[] {
  if (isSelected) {
    if (selectedIds.includes(assetId)) {
      return selectedIds;
    }
    if (maxMediaCount === 1) {
      return [assetId];
    }
    if (selectedIds.length >= maxMediaCount) {
      return selectedIds;
    }
    return [...selectedIds, assetId];
  }
  if (!selectedIds.includes(assetId)) {
    return selectedIds;
  }
  return selectedIds.filter(id => id !== assetId);
}

export function showNoVideosInAlbumMessage(postType: AlbumMediaPostType, hasAlbum: boolean, isLoading: boolean, filteredAssetCount: number): boolean {
  return isReelPostType(postType) && hasAlbum && !isLoading && filteredAssetCount === 0;
}

export function getDefaultPhotoSelectorTitle(postType: AlbumMediaPostType): string {
  if (getMaxMediaCount(postType) === 1) {
    return isVideoOnlyUploadForPostType(postType) ? "Choose a Video" : "Choose a Photo";
  }
  return "Select Photos";
}

export function getChannelPhotoSelectorTitle(postType: AlbumMediaPostType, channelLabel: string): string {
  if (getMaxMediaCount(postType) === 1) {
    const item = isVideoOnlyUploadForPostType(postType) ? "Video" : "Photo";
    return `Choose a ${item} for ${channelLabel}`;
  }
  return `Select photos for ${channelLabel}`;
}

export function isCaptionRequiredForPostType(postType: AlbumMediaPostType): boolean {
  return !isStoryPostType(postType);
}

export function isChannelSupportedForPostType(channel: SocialChannels, postType: AlbumMediaPostType): boolean {
  const isStory = postType === SocialPostType.Story || postType === SocialPostPublishType.Story;
  return !isStory || channel !== "tiktok";
}

export function filterChannelsForPostType(channels: Array<SocialChannels>, postType: AlbumMediaPostType): Array<SocialChannels> {
  return channels.filter(channel => isChannelSupportedForPostType(channel, postType));
}

/** True when width/height is at most REEL_STORY_MAX_ASPECT_RATIO (portrait, square, or mildly landscape). */
export function isPortraitOrSquare(resolution: ImgOrVideoResolution): boolean {
  if (resolution.h <= 0 || resolution.w <= 0) {
    return false;
  }
  return resolution.w / resolution.h <= REEL_STORY_MAX_ASPECT_RATIO;
}

export function validateManualPostInputs(params: { postType: AlbumMediaPostType; assetIds: string[]; caption: string }): ValidationResult {
  const { postType, assetIds, caption } = params;
  const maxMedia = getMaxMediaCount(postType);

  if (assetIds.length === 0) {
    if (isReelPostType(postType)) {
      return { ok: false, message: "Please select a video" };
    }
    if (isStoryPostType(postType)) {
      return { ok: false, message: "Please select a photo or video" };
    }
    return { ok: false, message: "Please select at least one photo" };
  }

  if (isSingleMediaPostType(postType) && assetIds.length !== 1) {
    return { ok: false, message: `${postType}s require exactly one item` };
  }

  if (assetIds.length > maxMedia) {
    return { ok: false, message: `Please select no more than ${maxMedia} item${maxMedia === 1 ? "" : "s"}` };
  }

  if (isReelPostType(postType)) {
    if (!assetIds.every(id => FileUtils.isVideoFileType(id))) {
      return { ok: false, message: "Reels require a single video" };
    }
  }

  if (isCaptionRequiredForPostType(postType) && !caption?.trim()) {
    return { ok: false, message: "Please add a caption" };
  }

  if (isCaptionRequiredForPostType(postType) && caption.length > MAX_CAPTION_LENGTH) {
    return { ok: false, message: `Caption must be ${MAX_CAPTION_LENGTH} characters or fewer` };
  }

  return { ok: true };
}

/** Story album images: validate portrait/square via CDN dimensions. Skips videos (processing may be incomplete). */
export async function validateManualPostMediaMetadata(params: { postType: AlbumMediaPostType; assetIds: string[] }): Promise<ValidationResult> {
  const { postType, assetIds } = params;

  if (!isStoryPostType(postType) || assetIds.length !== 1) {
    return { ok: true };
  }

  const assetId = assetIds[0];
  if (FileUtils.isVideoFileType(assetId)) {
    return { ok: true };
  }

  const imageUrl = FileUtils.getAdventureImagePath(assetId, false);
  try {
    const resolution = await getRemoteImageDimensions(imageUrl);
    if (!isPortraitOrSquare(resolution)) {
      return { ok: false, message: PORTRAIT_OR_SQUARE_MESSAGE };
    }
  } catch {
    // Dimensions unavailable (CDN/CORS/load failure) — do not block save.
    return { ok: true };
  }

  return { ok: true };
}

/** Reel album videos: validate portrait/square via CDN video metadata. */
export async function validateAlbumAssetForPostType(postType: AlbumMediaPostType, assetId: string): Promise<ValidationResult> {
  if (!isReelPostType(postType) || !FileUtils.isVideoFileType(assetId)) {
    return { ok: true };
  }

  const videoUrl = getAdventureVideoPath(assetId);
  try {
    const resolution = await getRemoteVideoDimensions(videoUrl);
    if (!isPortraitOrSquare(resolution)) {
      return { ok: false, message: PORTRAIT_OR_SQUARE_MESSAGE };
    }
  } catch {
    // Dimensions unavailable (processing/CORS/load failure) — do not block selection.
    return { ok: true };
  }

  return { ok: true };
}

/** Pre-upload validation for Reel/Story media (in-memory File, before server processing). */
export async function validateFileBeforeUpload(postType: AlbumMediaPostType, file: File): Promise<string | null> {
  if (!isReelPostType(postType) && !isStoryPostType(postType)) {
    return null;
  }

  const isVideo = FileUtils.isVideoFileType(file.name, file.type);
  if (isReelPostType(postType) && !isVideo) {
    return "Reels require a video file";
  }

  let resolution: ImgOrVideoResolution;
  if (isVideo) {
    resolution = await getVideoResolution(file);
  } else {
    resolution = await getImageResolution(file);
  }

  if (!isPortraitOrSquare(resolution)) {
    return PORTRAIT_OR_SQUARE_MESSAGE;
  }

  return null;
}
